import { useState, useRef, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/context/AuthContext";
import { saveLanguage, currentPreference, subscribePreference, resolveLanguage } from "@/i18n/language";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe } from "lucide-react";

const LANGS = [
  ["it", "Italiano"], ["en", "English"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"],
];

export default function LanguageSelector({ authed = true, className = "w-40" }) {
  const { i18n, t } = useTranslation();
  const preference = useSyncExternalStore(subscribePreference, currentPreference, currentPreference);
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const lifecycle = useRef(0);
  useEffect(() => { lifecycle.current += 1; saving.current = false; setBusy(false); return () => { lifecycle.current += 1; }; }, [user?.id]);
  const change = async (v) => {
    if (saving.current || v === preference) return;
    saving.current = true; setBusy(true);
    const version = lifecycle.current;
    const isCurrent = () => version === lifecycle.current;
    try {
      await saveLanguage(v, authed ? user?.id : null, authed ? language => api.put("/me/language", { language, resolved_language: resolveLanguage(language) }) : null, isCurrent);
    } catch {
      if (isCurrent()) toast.error(t("lang.saveError"));
    } finally { if (isCurrent()) { saving.current = false; setBusy(false); } }
  };
  return (
    <Select value={preference} onValueChange={change} disabled={busy}>
      <SelectTrigger className={className} data-testid="language-selector" aria-label={t("lang.select")} aria-busy={busy}>
        <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
        <SelectValue>{preference === "auto" ? t("lang.automaticShort", { language: LANGS.find(([code]) => code === i18n.language)?.[1] || "Italiano" }) : LANGS.find(([code]) => code === preference)?.[1]}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="auto" data-testid="lang-option-auto">{t("lang.automatic")}</SelectItem>
        {LANGS.map(([code, label]) => (
          <SelectItem key={code} value={code} data-testid={`lang-option-${code}`}>{label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
